import {codespacesPath} from '@github-ui/paths'
import {useQueryClient} from '@github-ui/react-query'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useState} from 'react'

import {
  copilotAgentDefaultBranchQueryKey,
  fetchCopilotAgentDefaultBranchRepository,
} from './fetch-copilot-agent-default-branch'

export function useCopilotAgent({
  owner,
  name,
  issueDatabaseId,
}: {
  owner: string
  name: string
  issueDatabaseId: number
}) {
  const queryClient = useQueryClient()

  const [vscsTarget] = useLocalStorage<string | undefined>('vscs_target', undefined)
  const [vscsTargetUrl] = useLocalStorage<string | undefined>('vscs_target_url', undefined)
  const [codespaceCreationError, setCodespaceCreationError] = useState('')

  const handleCopilotAgentModeSubmit = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) e.preventDefault()
      setCodespaceCreationError('')

      const newTab = ssrSafeWindow?.open('about:blank')

      try {
        const repository = await queryClient.fetchQuery({
          queryKey: copilotAgentDefaultBranchQueryKey(owner, name),
          queryFn: ({signal}) => fetchCopilotAgentDefaultBranchRepository(owner, name, signal),
        })

        if (!repository) {
          setCodespaceCreationError('Failed to fetch repository data. Please try again.')
          newTab?.close()
          return
        }

        const formData = new FormData()
        formData.append('codespace[repository_id]', String(repository.databaseId))
        formData.append('codespace[ref]', repository.defaultBranchRef?.name || 'main')
        formData.append('codespace[issue_id]', String(issueDatabaseId))

        if (vscsTarget) {
          formData.append('codespace[vscs_target]', vscsTarget)
        }
        if (vscsTargetUrl) {
          formData.append('codespace[vscs_target_url]', vscsTargetUrl)
        }

        const result = await verifiedFetch(codespacesPath(), {
          method: 'POST',
          body: formData,
        })
        const body = await result.json()
        if (!result.ok) {
          setCodespaceCreationError(body.error)
          newTab?.close()
        } else {
          newTab?.location.assign(`/codespaces/${body.codespace_name}`)
        }
      } catch {
        setCodespaceCreationError('Codespace creation failed.')
        newTab?.close()
      }
    },
    [queryClient, issueDatabaseId, owner, name, vscsTarget, vscsTargetUrl],
  )

  return {
    codespaceCreationError,
    setCodespaceCreationError,
    handleCopilotAgentModeSubmit,
  }
}
